import { prefs } from './storage.js';

export const RECENT_PHOTO_LIMIT = 12;
const RECENT_PHOTOS_KEY = 'recentPhotos';
const IMAGE_EXPORT_MIME = 'image/webp';
const IMAGE_EXPORT_QUALITY = 0.82;
const IMAGE_MAX_EDGE = 1600;

export const PHOTO_INPUT_ACCEPT =
  'image/heic,image/heif,image/jpeg,image/jpg,image/png,image/webp,image/gif';

/**
 * The "Files" picker also takes photos and videos: they are sent byte-for-byte, without
 * the client-side WebP re-encode of the "Photo" picker (Telegram's "send as file").
 */
export const FILE_INPUT_ACCEPT = [
  'image/*',
  'video/*',
  '.heic',
  '.heif',
  '.pdf',
  '.txt',
  '.rtf',
  '.csv',
  '.md',
  '.json',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.zip',
  '.rar',
  '.7z',
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/json',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-zip-compressed',
].join(',');

export interface RecentPhoto {
  id: string;
  fileName: string;
  mime: string;
  dataUrl: string;
  createdAt: string;
}

const SAFE_IMAGE_DATA_URL =
  /^data:(image\/(?:webp|png|jpeg|jpg|gif|heic|heif));base64,([a-z0-9+/=]+)$/i;

function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function replaceExtension(fileName: string, nextExt: string): string {
  const clean = fileName.trim() || 'photo';
  const dot = clean.lastIndexOf('.');
  const base = dot > 0 ? clean.slice(0, dot) : clean;
  return `${base}.${nextExt}`;
}

function isRecentPhoto(value: unknown): value is RecentPhoto {
  if (!value || typeof value !== 'object') return false;
  const photo = value as Partial<RecentPhoto>;
  return (
    typeof photo.id === 'string'
    && typeof photo.fileName === 'string'
    && typeof photo.mime === 'string'
    && typeof photo.dataUrl === 'string'
    && isSafeImageDataUrl(photo.dataUrl)
    && typeof photo.createdAt === 'string'
  );
}

export function normalizeRecentPhotoList(value: unknown, limit = RECENT_PHOTO_LIMIT): RecentPhoto[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isRecentPhoto)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, limit);
}

export function upsertRecentPhotoList(
  current: RecentPhoto[],
  photo: RecentPhoto,
  limit = RECENT_PHOTO_LIMIT,
): RecentPhoto[] {
  return [photo, ...current.filter((item) => item.dataUrl !== photo.dataUrl)].slice(0, limit);
}

export function readRecentPhotos(limit = RECENT_PHOTO_LIMIT): RecentPhoto[] {
  const raw = prefs.get(RECENT_PHOTOS_KEY);
  if (!raw) return [];
  try {
    return normalizeRecentPhotoList(JSON.parse(raw), limit);
  } catch {
    return [];
  }
}

function writeRecentPhotos(list: RecentPhoto[]): void {
  prefs.set(RECENT_PHOTOS_KEY, JSON.stringify(list));
}

export function storeRecentPhoto(photo: RecentPhoto, limit = RECENT_PHOTO_LIMIT): RecentPhoto[] {
  const next = upsertRecentPhotoList(readRecentPhotos(limit), photo, limit);
  writeRecentPhotos(next);
  return next;
}

export function touchRecentPhoto(photoId: string): RecentPhoto[] {
  const current = readRecentPhotos();
  const photo = current.find((item) => item.id === photoId);
  if (!photo) return current;
  return storeRecentPhoto({ ...photo, createdAt: new Date().toISOString() });
}

export function dataUrlToBase64(dataUrl: string): string {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex === -1 ? dataUrl : dataUrl.slice(commaIndex + 1);
}

export function decodeImageDataUrl(dataUrl: string): { mime: string; base64: string } | null {
  const match = SAFE_IMAGE_DATA_URL.exec(dataUrl);
  if (!match) return null;
  const [, mime = '', base64 = ''] = match;
  return { mime, base64 };
}

export function isSafeImageDataUrl(dataUrl: string): boolean {
  return decodeImageDataUrl(dataUrl) !== null;
}

export function imageDataUrlToObjectUrl(dataUrl: string): string | null {
  const decoded = decodeImageDataUrl(dataUrl);
  if (!decoded || typeof atob !== 'function' || typeof Blob === 'undefined') return null;
  const binary = atob(decoded.base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: decoded.mime }));
}

export function recentPhotoFromDataUrl(dataUrl: string, fileName: string, mime: string): RecentPhoto {
  return {
    id: newId('recent-photo'),
    fileName,
    mime,
    dataUrl,
    createdAt: new Date().toISOString(),
  };
}

export async function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  });
}

async function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode image'));
    };
    img.src = objectUrl;
  });
}

export async function imageFileToDataUrl(file: File): Promise<{
  dataUrl: string;
  fileName: string;
  mime: string;
}> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    const dataUrl = await readFileAsDataUrl(file);
    return { dataUrl, fileName: file.name, mime: file.type || 'application/octet-stream' };
  }

  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(image.width, image.height));
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));

  const context = canvas.getContext('2d');
  if (!context) {
    const dataUrl = await readFileAsDataUrl(file);
    return { dataUrl, fileName: file.name, mime: file.type || 'application/octet-stream' };
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL(IMAGE_EXPORT_MIME, IMAGE_EXPORT_QUALITY);
  return {
    dataUrl,
    fileName: replaceExtension(file.name || 'photo', 'webp'),
    mime: IMAGE_EXPORT_MIME,
  };
}
